import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 60000);
    return () => clearInterval(t);
  }, [fetch]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    // mousedown for desktop, touchstart for mobile — otherwise a tap on the
    // page background never dismisses the panel on touch devices.
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open]);

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    setOpen(false);
    if (n.type === "provider_reply" && n.payload?.idea_id) {
      navigate(`/idea/${n.payload.idea_id}#field-notes`);
    }
    if (n.type === "regrow" && n.payload?.idea_id) {
      navigate(`/idea/${n.payload.idea_id}`);
    }
    if ((n.type === "comment" || n.type === "mention") && n.payload?.idea_id) {
      // Deep-link to the seed's comments section so the author lands right
      // where the conversation is happening.
      navigate(`/idea/${n.payload.idea_id}#comments`);
    }
    fetch();
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      fetch();
    } catch {}
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        title="Notifications"
        className="relative p-2 rounded-full hover:bg-[#B79256]/12 text-[#2A2823]/70 hover:text-[#2A2823] transition-colors"
      >
        <Bell className="w-5 h-5" strokeWidth={1.6} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B79256] text-[#FBF8F1] text-[10px] font-semibold flex items-center justify-center border border-[#FBF8F1]"
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <>
          {/* Mobile-only tap-shield behind the panel. On phones the panel
              is FIXED (not absolute) so the global body overflow-x:hidden
              can't clip it — we anchor to the viewport, not the nav. */}
          <div
            aria-hidden="true"
            data-testid="notifications-scrim"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 sm:hidden bg-black/10"
          />
          <div
            data-testid="notifications-panel"
            className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 max-h-[calc(100vh-5rem)] overflow-hidden rounded-2xl bg-[#FBF8F1] border border-[#B79256]/30 shadow-[0_20px_50px_-20px_rgba(42,40,35,0.35)] p-2 z-50"
          >
          <div className="flex items-center justify-between px-3 py-2">
            <div className="font-serif-display text-lg text-[#2A2823]">Recent</div>
            {unread > 0 && (
              <button
                type="button"
                data-testid="notifications-mark-all"
                onClick={markAll}
                className="accent-label text-xs text-[#4E5A3D] hover:text-[#3E4830]"
              >
                Mark all read
              </button>
            )}
          </div>
          {loading ? (
            <div className="px-3 py-6 text-sm text-[#2A2823]/50">Loading…</div>
          ) : notifs.length === 0 ? (
            <div data-testid="notifications-empty" className="px-3 py-8 text-center text-sm text-[#2A2823]/55">
              Nothing new — a quiet grove is a good grove.
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {notifs.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => openNotif(n)}
                    className={`w-full text-left px-3 py-3 rounded-xl transition-colors ${
                      n.read ? "hover:bg-[#B79256]/8" : "bg-[#B79256]/8 hover:bg-[#B79256]/15"
                    }`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className={`accent-label ${n.read ? "text-[#2A2823]/50" : "text-[#B79256]"}`}>
                        {n.type === "provider_reply"
                          ? "Provider replied"
                          : n.type === "regrow"
                            ? "Regrown"
                            : n.type === "comment"
                              ? "New comment"
                              : n.type === "mention"
                                ? "You were mentioned"
                                : "Update"}
                      </span>
                      <span className="text-xs text-[#2A2823]/40">
                        {new Date(n.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {n.type === "provider_reply" && (
                      <>
                        <p className="text-sm text-[#2A2823]/85 mt-1">
                          <b className="font-medium">{n.payload?.provider_name}</b>
                          {" replied to your Field Note on "}
                          <span className="font-serif-display">{n.payload?.idea_title}</span>
                        </p>
                        {n.payload?.preview && (
                          <p className="text-xs text-[#2A2823]/55 mt-1 italic line-clamp-2">
                            "{n.payload.preview}"
                          </p>
                        )}
                      </>
                    )}
                    {n.type === "regrow" && (
                      <>
                        <p className="text-sm text-[#2A2823]/85 mt-1">
                          <b className="font-medium">{n.payload?.regrower_name}</b>
                          {" regrew your seed "}
                          <span className="font-serif-display">{n.payload?.idea_title}</span>
                        </p>
                        {n.payload?.regrower_note && (
                          <p className="text-xs text-[#2A2823]/55 mt-1 italic line-clamp-2">
                            "{n.payload.regrower_note}"
                          </p>
                        )}
                      </>
                    )}
                    {n.type === "comment" && (
                      <>
                        <p className="text-sm text-[#2A2823]/85 mt-1">
                          <b className="font-medium">{n.payload?.commenter_name}</b>
                          {" commented on your seed "}
                          <span className="font-serif-display">{n.payload?.idea_title}</span>
                        </p>
                        {n.payload?.preview && (
                          <p className="text-xs text-[#2A2823]/55 mt-1 italic line-clamp-2">
                            "{n.payload.preview}"
                          </p>
                        )}
                      </>
                    )}
                    {n.type === "mention" && (
                      <>
                        <p className="text-sm text-[#2A2823]/85 mt-1">
                          <b className="font-medium">{n.payload?.mentioner_name}</b>
                          {" mentioned you in a comment on "}
                          <span className="font-serif-display">{n.payload?.idea_title}</span>
                        </p>
                        {n.payload?.preview && (
                          <p className="text-xs text-[#2A2823]/55 mt-1 italic line-clamp-2">
                            "{n.payload.preview}"
                          </p>
                        )}
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        </>
      )}
    </div>
  );
}
